/**
 * Fix 22 — Public Schedule section.
 * Renders the current Asia/Dubai month's published events.
 * HARD contract: returns `null` on load, on API failure, and when
 * the current month has zero published events.  No wrapper, no
 * heading, no skeleton — the section literally does not exist in
 * the DOM in those states.
 *
 * Fix 23 — Compact typography (~20% smaller), weekday derived from
 * event_date via Sakamoto (no browser TZ), Arabic month/weekday/city
 * localization via ``@/lib/scheduleLocale``.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { flagFromCode } from "@/lib/flagFromCode";
import {
  weekdayLabel,
  monthLabel,
  monthLabelFromIndex,
  cityLabel,
} from "@/lib/scheduleLocale";
import { findCountry } from "@/constants/countries";
import { getDubaiToday, msUntilNextDubaiMidnight, classifyEventDate } from "@/lib/dubaiDate";

export default function Schedule() {
  const { t, i18n } = useTranslation();
  const [state, setState] = useState({ status: "loading", items: [], month: "", month_index: 0, year: 0 });
  const [today, setToday] = useState(getDubaiToday());

  // Data fetch (extracted so we can refetch on Dubai month rollover).
  const load = async () => {
    try {
      const { data } = await api.get("/schedule/current");
      setState({
        status: "ready",
        items: Array.isArray(data?.items) ? data.items : [],
        month: data?.month || "",
        month_index: Number(data?.month_index) || 0,
        year: Number(data?.year) || 0,
      });
    } catch {
      setState({ status: "failed", items: [], month: "", month_index: 0, year: 0 });
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => { if (!cancelled) await load(); })();
    return () => { cancelled = true; };
  }, []);

  // Midnight timer + visibility handler.  Recomputes Dubai date at the
  // next Dubai midnight (never a 24 h fixed interval, even if the tab
  // was opened at 23:50), refreshes on tab return, cleans up on unmount.
  useEffect(() => {
    let timerId = null;
    const applyToday = () => {
      const next = getDubaiToday();
      setToday((prev) => (prev === next ? prev : next));
      const prevMonth = (state.year && state.month_index)
        ? `${state.year}-${String(state.month_index).padStart(2, "0")}`
        : "";
      const nextMonth = next.slice(0, 7);
      if (prevMonth && nextMonth !== prevMonth) {
        // Dubai month rolled over — refetch current-month payload.
        load();
      }
    };
    const scheduleNext = () => {
      const ms = msUntilNextDubaiMidnight();
      timerId = setTimeout(() => { applyToday(); scheduleNext(); }, ms);
    };
    scheduleNext();
    const onVisibility = () => { if (!document.hidden) applyToday(); };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("focus", applyToday);
    return () => {
      if (timerId) clearTimeout(timerId);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("focus", applyToday);
    };
  }, [state.year, state.month_index]);

  // HARD CONTRACT — no outer container on any non-ready state.
  if (state.status !== "ready") return null;
  if (!state.items.length) return null;

  const locale = i18n.language?.startsWith("ar") ? "ar" : "en";
  const isAr = locale === "ar";
  const headingMonth = monthLabelFromIndex(state.month_index, locale) || state.month;
  const sectionLabel = isAr ? "جدول DJ SPY" : (t("schedule.section_label", "DJ SPY SCHEDULE"));

  return (
    <section
      data-testid="home-schedule"
      className="relative py-10 md:py-14 border-t border-white/10"
    >
      <div className="max-w-6xl mx-auto px-5 md:px-6">
        <div className="mb-4 md:mb-6 flex items-end justify-between gap-4">
          <div>
            <p
              className={`font-mono text-[10px] tracking-[0.3em] text-red-brand mb-1.5 ${isAr ? "text-right" : ""}`}
              dir="ltr"
              data-testid="schedule-section-label"
            >
              // {sectionLabel}
            </p>
            <h2 className="font-anton text-2xl sm:text-3xl md:text-4xl tracking-tight leading-none" data-testid="schedule-heading">
              <bdi>{headingMonth} {state.year}</bdi>
            </h2>
          </div>
          <span className={`text-white/40 ${isAr ? "text-xs" : "font-mono text-[10px] tracking-[0.25em]"}`} data-testid="schedule-count">
            <bdi>{state.items.length} {isAr ? "مواعيد" : "DATES"}</bdi>
          </span>
        </div>

        <ul className="grid grid-cols-1 md:grid-cols-2 md:gap-x-8 border-t border-white/10" data-testid="schedule-list">
          {state.items.map((ev) => {
            const [, , dd] = (ev.event_date || "").split("-");
            const wk = weekdayLabel(ev.event_date, locale);
            const mLabel = monthLabel(ev.event_date, locale);
            const flag = flagFromCode(ev.country_code);
            const country = findCountry(ev.country_code);
            const countryDisplay = isAr && country ? country.name_ar : (ev.country || (country ? country.name : ""));
            const cityDisplay = cityLabel(ev.city, locale);
            const status = classifyEventDate(ev.event_date);
            const statusLabel = status === "today" ? (isAr ? "الليلة" : "TONIGHT") : status === "past" ? (isAr ? "انتهى" : "PAST") : "";
            return (
              <li
                key={ev.id}
                className={`flex items-center gap-3 py-1.5 border-b border-white/10 min-h-[44px] ${status === "past" ? "opacity-55" : ""} ${status === "today" ? "schedule-today" : ""}`}
                data-testid={`schedule-item-${ev.id}`}
                data-status={status}
              >
                {status === "today" ? <span className="schedule-today__shine" aria-hidden="true" /> : null}
                <div className={`${isAr ? "w-[76px]" : "w-[52px]"} shrink-0 flex items-baseline gap-1`} dir="ltr">
                  <span className="font-anton text-xl leading-none text-white"><bdi>{dd || ""}</bdi></span>
                  <span className={`text-red-brand leading-none ${isAr ? "text-[11px]" : "font-mono text-[9px] tracking-[0.15em]"}`}>
                    <bdi data-testid={`schedule-month-${ev.id}`}>{mLabel}</bdi>
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-anton text-[15px] md:text-base text-white uppercase tracking-tight truncate leading-tight">
                    {ev.public_url ? (
                      <a href={ev.public_url} target="_blank" rel="noopener noreferrer" className="hover:text-red-brand transition-colors" data-testid={`schedule-link-${ev.id}`}>
                        {ev.venue}
                      </a>
                    ) : ev.venue}
                  </div>
                  <div className={`text-white/55 truncate leading-tight mt-0.5 ${isAr ? "text-xs" : "text-[11px] font-mono"}`}>
                    <bdi data-testid={`schedule-weekday-${ev.id}`}>{wk}</bdi>
                    {" · "}
                    {flag ? <span className="me-1" role="img" aria-label={countryDisplay} title={countryDisplay}>{flag}</span> : null}
                    <bdi>{cityDisplay || countryDisplay}</bdi>
                    {ev.event_time ? <span className="text-white/40" data-testid={`schedule-time-${ev.id}`}> · <bdi>{ev.event_time}</bdi></span> : null}
                  </div>
                </div>
                {statusLabel ? (
                  <span
                    className={`shrink-0 px-1.5 py-0.5 rounded border ${isAr ? "text-[10px]" : "font-mono text-[9px] tracking-[0.2em]"} ${status === "today" ? "schedule-today__badge" : "text-white/45 border-white/15"}`}
                    data-testid={`schedule-status-${ev.id}`}
                  >
                    <bdi>{statusLabel}</bdi>
                  </span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
